<template>
	<v-container class="fill-height" max-width="420">
		<v-row justify="center">
			<v-col cols="12">
				<div class="text-center mb-6">
					<img src="/favicon.svg" alt="" width="72" height="72" style="border-radius: 22.5%" />
					<h1 class="text-h5 mt-2">Bed</h1>
				</div>
				<v-card v-if="leaving">
					<v-card-text class="text-center py-8">
						<v-progress-circular indeterminate class="mb-4" />
						<div>Taking you to sign in…</div>
						<div class="text-caption mt-2">
							One password for all of Stephen's apps, at id.richworks.dev.
						</div>
					</v-card-text>
				</v-card>
				<v-card v-else>
					<v-card-title>Sign in</v-card-title>
					<v-card-text>
						<!-- The ordinary way in is the one sign-in service; this form is for a device
						     that carries the API token instead, like a Shortcut. Reach it with
						     /login?token=1 when the service itself cannot be used. -->
						<v-alert v-if="ssoAvailable" type="info" variant="tonal" density="compact" class="mb-4">
							<a href="/sso/start?next=%2F">Sign in with your richworks password</a> instead,
							or use the API token below.
						</v-alert>
						<!-- A real <form> with autocomplete attributes is what password
						     managers (Dashlane, 1Password, iOS/Android keychains) key on
						     to offer save + autofill. The username is not checked by the
						     backend — it exists so managers have a complete credential
						     pair to store against bed.richworks.dev. -->
						<form method="post" @submit.prevent="submit">
							<v-text-field
								v-model="username"
								name="username"
								autocomplete="username"
								label="Username"
								hint="Not checked — just for your password manager"
								persistent-hint
								density="compact"
								class="mb-3"
							/>
							<v-text-field
								v-model="token"
								name="password"
								:type="showToken ? 'text' : 'password'"
								autocomplete="current-password"
								label="API token"
								density="compact"
								:error-messages="error ? [error] : []"
								:append-inner-icon="showToken ? 'mdi-eye-off' : 'mdi-eye'"
								@click:append-inner="showToken = !showToken"
							/>
							<v-btn type="submit" color="primary" block class="mt-4" :loading="checking" :disabled="!token">
								Sign in
							</v-btn>
						</form>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api } from "../api.js";

var router = useRouter();
var route = useRoute();

var username = ref("bed");
var token = ref("");
var showToken = ref(false);
var error = ref(null);
var checking = ref(false);
// While the browser is on its way to the sign-in service, so the form does not flash up first.
var leaving = ref(false);
var ssoAvailable = ref(false);

// Probe /api/status with (optionally) a candidate token. Plain fetch on
// purpose: api.request() redirects here on 401, which would loop.
async function probe(candidate) {
	var headers = candidate ? { "Authorization": "Bearer " + candidate } : {};
	var res = await fetch("/api/status", { headers });
	return res.status;
}

// What this page does, in order:
//
//   already allowed  the probe succeeds because this browser has a session from the one sign-in
//                    service, or because the backend runs with no API_TOKEN at all (a PC). The
//                    sessionStorage flag lets the router guard wave this session through.
//   not allowed      leave for the sign-in service, which asks for the one password. Only when
//                    online: offline there is nothing to redirect to, so the form stays.
//   ?token=1         skip all that and show the API token form, for a device that uses one or for
//                    when the sign-in service cannot be reached.
onMounted(async () => {
	if (api.getToken()) { return; }
	if (route.query.token) { ssoAvailable.value = true; return; }
	try {
		var status = await probe(null);
		if (status < 400) {
			sessionStorage.setItem("bed.authOpen", "1");
			sessionStorage.removeItem("bed.ssoTries");
			router.replace({ name: "dashboard" });
			return;
		}
		if (status === 401 && navigator.onLine) {
			// Stop if this has already been tried twice in this tab: something between here and
			// the server is swallowing the hand-off (a stale service worker, or cookies refused),
			// and trying again would only flash this page over and over. Offer the token instead.
			var tries = Number(sessionStorage.getItem("bed.ssoTries") ?? 0) + 1;
			sessionStorage.setItem("bed.ssoTries", String(tries));
			if (tries > 2) {
				ssoAvailable.value = true;
				error.value = "Signing in went round in a circle. Close the app and open it again, or use the API token.";
				return;
			}
			leaving.value = true;
			window.location.assign("/sso/start?next=%2F");
			return;
		}
		ssoAvailable.value = true;
	} catch (e) {
		// Offline: no point sending the browser anywhere, so offer the token form.
		error.value = "Can't reach the bed. You can sign in with the API token.";
	}
});

async function submit() {
	error.value = null;
	checking.value = true;
	try {
		var status = await probe(token.value.trim());
		if (status === 401) {
			error.value = "Token rejected — check API_TOKEN on the server.";
			return;
		}
		if (status >= 400) {
			error.value = "Server error (HTTP " + status + ") — try again.";
			return;
		}
		api.setToken(token.value.trim());
		router.replace({ name: "dashboard" });
	} catch (e) {
		error.value = "Can't reach the server — are you online?";
	} finally {
		checking.value = false;
	}
}
</script>
